import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Zap, Phone, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="min-h-screen bg-[rgb(0,55,32)] flex flex-col" data-testid="landing-page">
      {/* Header */}
      <header className="w-full py-6 px-6 flex justify-between items-center z-10">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="MM Renovations" className="h-12 w-auto object-contain brightness-150" />
          <div>
            <h1 className="text-white font-bold text-lg leading-tight">Mat Moffatt</h1>
            <p className="text-white/50 text-xs tracking-wider uppercase">Renovations LTD</p>
          </div>
        </div>
        <a
          href="tel:613-295-9098"
          className="hidden sm:flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
          data-testid="landing-phone"
        >
          <Phone className="w-4 h-4" />
          613-295-9098
        </a>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="text-center mb-12">
          <h2 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            How Can We Help?
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Select a service to explore what we offer
          </p>
        </div>

        {/* Two Cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl w-full">
          {/* Construction Card */}
          <div
            onClick={() => navigate('/construction')}
            onMouseEnter={() => setHoveredCard('construction')}
            onMouseLeave={() => setHoveredCard(null)}
            className="group relative cursor-pointer rounded-2xl overflow-hidden bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-500 hover:border-white/30 hover:scale-[1.02]"
            data-testid="landing-construction-card"
          >
            <div className="p-8 sm:p-10 flex flex-col items-center text-center min-h-[320px] justify-center">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${hoveredCard === 'construction' ? 'bg-white scale-110' : 'bg-white/10'}`}>
                <Building2 className={`w-10 h-10 transition-colors duration-500 ${hoveredCard === 'construction' ? 'text-[rgb(0,55,32)]' : 'text-white'}`} />
              </div>
              <h3 className="text-white text-2xl sm:text-3xl font-bold mb-3">
                General Construction
              </h3>
              <p className="text-white/60 mb-6 max-w-xs leading-relaxed">
                Full home renovations, SDU creations, kitchens, bathrooms, and complete remodels
              </p>
              <div className={`flex items-center gap-2 text-white font-semibold transition-all duration-300 ${hoveredCard === 'construction' ? 'gap-4' : ''}`}>
                Explore Services
                <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${hoveredCard === 'construction' ? 'translate-x-1' : ''}`} />
              </div>
            </div>
            {/* Decorative gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2D5016] via-[#4A7F25] to-[#2D5016] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>

          {/* Electrical Card */}
          <div
            onClick={() => navigate('/electrical')}
            onMouseEnter={() => setHoveredCard('electrical')}
            onMouseLeave={() => setHoveredCard(null)}
            className="group relative cursor-pointer rounded-2xl overflow-hidden bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-500 hover:border-white/30 hover:scale-[1.02]"
            data-testid="landing-electrical-card"
          >
            <div className="p-8 sm:p-10 flex flex-col items-center text-center min-h-[320px] justify-center">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${hoveredCard === 'electrical' ? 'bg-[#FBBC04] scale-110' : 'bg-white/10'}`}>
                <Zap className={`w-10 h-10 transition-colors duration-500 ${hoveredCard === 'electrical' ? 'text-[rgb(0,55,32)]' : 'text-[#FBBC04]'}`} />
              </div>
              <h3 className="text-white text-2xl sm:text-3xl font-bold mb-3">
                Electrical Services
              </h3>
              <p className="text-white/60 mb-6 max-w-xs leading-relaxed">
                Licensed electrical contractor — residential & commercial wiring, panels, EV chargers, and more
              </p>
              <div className={`flex items-center gap-2 text-white font-semibold transition-all duration-300 ${hoveredCard === 'electrical' ? 'gap-4' : ''}`}>
                Explore Services
                <ArrowRight className={`w-5 h-5 transition-transform duration-300 ${hoveredCard === 'electrical' ? 'translate-x-1' : ''}`} />
              </div>
            </div>
            {/* Decorative gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FBBC04] via-[#F5A623] to-[#FBBC04] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 text-center">
        <p className="text-white/30 text-sm">&copy; 2026 Mat Moffatt Renovations LTD. All rights reserved.</p>
        <div className="flex items-center justify-center gap-4 mt-2 text-xs text-white/30">
          <a href="/privacy-policy" className="hover:text-white/60 transition-colors">Privacy Policy</a>
          <span>|</span>
          <a href="/terms-of-service" className="hover:text-white/60 transition-colors">Terms of Service</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
